/**
 * Where the waiting goes on ServiceBox and Alpha.
 *
 * Every operation is timed — the ones the garage's proxy performs (transfert Alpha,
 * création de RDV, suppression) and the ones the backend performs straight against
 * ServiceBox (pousser des interventions, relire un RDV). This page answers three
 * questions in the order they get asked: was today worse than yesterday, which action
 * costs the most, and who is paying for it.
 *
 * Total waiting time leads, not the average: an average hides ten quick pushes behind
 * the one that took 40 s, and it is the cumulated wait that the réception actually feels.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  proxyAdminApi,
  type OperationTimings,
  type SlowOperation,
  type TimingScope,
  type TimingStats,
} from "@services/api/proxyAdmin";
import { showNotification } from "context/notification.service";

const RANGES = [7, 30, 90];

// Whose time is on the chart, and "human" by default: the proxy's autotest was the most
// expensive operation of the fortnight and not one person ever waited for it.
const SCOPES: { value: TimingScope; label: string; hint: string }[] = [
  { value: "human", label: "Attente des utilisateurs", hint: "Ce que quelqu'un a réellement attendu devant son écran." },
  { value: "auto", label: "Trafic automatique", hint: "Autotests, synchros, tâches planifiées : personne ne les attend, mais elles occupent le proxy." },
  { value: "all", label: "Tout", hint: "Toutes les opérations chronométrées, humaines et automatiques." },
];

type SortMode = "slowest" | "recent";

// One series, one hue: identity is carried by the title, not by colour.
const BAR = "#6366F1";
const BAR_HOVER = "#4F46E5";

function fmtDuration(ms: number): string {
  if (!ms) return "—";
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.round((ms % 60000) / 1000);
  if (minutes < 60) return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

function fmtDay(key: string): string {
  // Midday avoids the UTC/local off-by-one when turning a plain date into a label.
  return new Date(`${key}T12:00:00`)
    .toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
    .replace(".", "");
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })} ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
}

/** Automated work has no operator: say so rather than showing an empty cell. */
function userLabel(email: string): string {
  return email || "Automatique (synchros, tâches planifiées)";
}

const OPERATION_LABELS: Record<string, string> = {
  transfer_to_alpha: "Transfert Alpha (création de l'OR)",
  create_rdv: "Création du RDV ServiceBox",
  delete_rdv: "Suppression du RDV ServiceBox",
  push_interventions: "Pousser des interventions",
  lecture_interventions: "Lecture des interventions du RDV",
  set_etat: "Changement d'état ServiceBox",
  fetch_estimation: "Récupération du devis",
  options: "Options / listes ServiceBox",
  test_connection: "Test de connexion",
  selftest: "Autotest du proxy",
};

const STEP_LABELS: Record<string, string> = {
  ouverture_rdv: "ouverture du RDV",
  enregistrement_relecture: "enregistrement + relecture",
  panier_forfait: "panier (code forfait)",
};

function opLabel(op: string): string {
  return OPERATION_LABELS[op] || op;
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-row items-center gap-1 bg-neutral-100 rounded-lg p-1 w-fit">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-3 py-1 rounded-md text-h4 transition-colors ${
            value === o.value ? "bg-white text-neutral-800 font-medium shadow-sm" : "text-neutral-500 hover:text-neutral-700"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-1 bg-white border border-neutral-200 rounded-xl px-4 py-3 flex-1 min-w-150">
      <span className="text-h4 text-neutral-400">{label}</span>
      <span className="text-xl font-semibold text-neutral-800">{value}</span>
      {hint && <span className="text-h5 text-neutral-400">{hint}</span>}
    </div>
  );
}

/** Daily cumulated waiting time. One series, so no legend — the title names it. */
function DailyWaitChart({ daily }: { daily: OperationTimings["daily"] }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const max = Math.max(...daily.map((d) => d.total_ms), 1);
  const chartH = 150;
  const slotW = 100 / Math.max(daily.length, 1);
  const peak = daily.reduce((best, d, i) => (d.total_ms > daily[best].total_ms ? i : best), 0);

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-col gap-3">
      <div className="flex flex-row items-baseline justify-between gap-3">
        <span className="text-h2 font-semibold text-neutral-700">Temps d'attente cumulé par jour</span>
        <span className="text-h4 text-neutral-400">ServiceBox + Alpha, toutes opérations</span>
      </div>

      <div className="relative" style={{ height: chartH + 24 }}>
        <div className="absolute inset-x-0 top-0 flex flex-row items-end" style={{ height: chartH }}>
          {daily.map((d, i) => {
            const h = Math.max((d.total_ms / max) * (chartH - 10), d.total_ms ? 2 : 0);
            return (
              <div
                key={d.day}
                className="flex flex-col justify-end items-center h-full cursor-default"
                style={{ width: `${slotW}%` }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                {/* 2px gap between bars, 4px rounded top anchored on the baseline. */}
                <div
                  className="rounded-t"
                  style={{
                    width: "calc(100% - 2px)",
                    height: h,
                    background: hovered === i ? BAR_HOVER : BAR,
                    opacity: d.total_ms ? 1 : 0,
                  }}
                />
              </div>
            );
          })}
        </div>
        <div className="absolute inset-x-0 border-t border-neutral-200" style={{ top: chartH }} />
        <div className="absolute inset-x-0 flex flex-row" style={{ top: chartH + 4 }}>
          {daily.map((d, i) => (
            <div key={d.day} className="text-center text-h6 text-neutral-400" style={{ width: `${slotW}%` }}>
              {/* Only the ends and the peak are labelled: a label on every bar is noise. */}
              {i === 0 || i === daily.length - 1 || i === peak ? fmtDay(d.day) : ""}
            </div>
          ))}
        </div>

        {hovered !== null && daily[hovered] && (
          <div
            className="absolute -top-1 px-3 py-2 rounded-lg bg-neutral-800 text-white text-h5 shadow-lg pointer-events-none z-10"
            style={{
              left: `${Math.min(Math.max(hovered * slotW, 0), 100 - slotW * 6)}%`,
            }}
          >
            <div className="font-medium">{fmtDay(daily[hovered].day)}</div>
            <div>{fmtDuration(daily[hovered].total_ms)} d'attente cumulée</div>
            <div className="opacity-70">
              {daily[hovered].count} opération(s) · moy. {fmtDuration(daily[hovered].avg_ms)} · p95{" "}
              {fmtDuration(daily[hovered].p95_ms)}
              {daily[hovered].failures ? ` · ${daily[hovered].failures} échec(s)` : ""}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatsTable({
  title,
  subtitle,
  rows,
}: {
  title: string;
  subtitle: string;
  rows: { key: string; label: string; stats: TimingStats }[];
}) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-col gap-2 flex-1 min-w-350">
      <div>
        <div className="text-h2 font-semibold text-neutral-700">{title}</div>
        <div className="text-h4 text-neutral-400">{subtitle}</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-h3 text-neutral-400 py-2">Rien sur la période.</div>
      ) : (
        <table className="w-full text-h4">
          <thead>
            <tr className="text-neutral-400 text-left">
              <th className="font-normal py-1">&nbsp;</th>
              <th className="font-normal py-1 text-right">Total</th>
              <th className="font-normal py-1 text-right">Moy.</th>
              <th className="font-normal py-1 text-right">p95</th>
              <th className="font-normal py-1 text-right">Max</th>
              <th className="font-normal py-1 text-right">Nb</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-t border-neutral-100">
                <td className="py-1.5 pr-2 text-neutral-700">{row.label}</td>
                <td className="py-1.5 text-right font-medium text-neutral-800">{fmtDuration(row.stats.total_ms)}</td>
                <td className="py-1.5 text-right text-neutral-600">{fmtDuration(row.stats.avg_ms)}</td>
                <td className="py-1.5 text-right text-neutral-600">{fmtDuration(row.stats.p95_ms)}</td>
                <td className="py-1.5 text-right text-neutral-600">{fmtDuration(row.stats.max_ms)}</td>
                <td className="py-1.5 text-right text-neutral-400">{row.stats.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function OperationsList({
  slowest,
  recent,
  sort,
  onSortChange,
}: {
  slowest: SlowOperation[];
  recent: SlowOperation[];
  sort: SortMode;
  onSortChange: (s: SortMode) => void;
}) {
  const rows = sort === "slowest" ? slowest : recent;
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-col gap-2">
      <div className="flex flex-row items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-h2 font-semibold text-neutral-700">Opérations</div>
          <div className="text-h4 text-neutral-400">
            {sort === "slowest"
              ? "Les 25 pires attentes de la période, avec le détail par étape quand l'opération l'a mesuré."
              : "Les 25 dernières opérations, la plus récente en haut."}
          </div>
        </div>
        <Segmented
          value={sort}
          options={[
            { value: "slowest" as SortMode, label: "Les plus longues" },
            { value: "recent" as SortMode, label: "Les plus récentes" },
          ]}
          onChange={onSortChange}
        />
      </div>
      {rows.length === 0 ? (
        <div className="text-h3 text-neutral-400 py-2">Rien sur la période.</div>
      ) : (
        <div className="flex flex-col">
          {rows.map((row, i) => (
            <div
              key={`${row.timestamp}-${i}`}
              className="flex flex-row items-baseline gap-3 py-1.5 border-t border-neutral-100 text-h4"
            >
              <span className="text-neutral-400 w-100 shrink-0">{fmtTime(row.timestamp)}</span>
              <span className="font-medium text-neutral-800 w-200 shrink-0">{fmtDuration(row.duration_ms)}</span>
              <span className="flex-1 min-w-0">
                <span className="text-neutral-700">{opLabel(row.operation)}</span>
                {!row.success && <span className="text-red-600"> — échec</span>}
                {Object.keys(row.steps || {}).length > 0 && (
                  <span className="text-neutral-400">
                    {" — "}
                    {Object.entries(row.steps)
                      .sort((a, b) => b[1] - a[1])
                      .map(([name, ms]) => `${STEP_LABELS[name] || name} ${fmtDuration(ms)}`)
                      .join(", ")}
                  </span>
                )}
              </span>
              <span className="text-neutral-400 truncate w-250 shrink-0 text-right">{userLabel(row.operator_email)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ServiceBoxTimings() {
  const [days, setDays] = useState(30);
  const [scope, setScope] = useState<TimingScope>("human");
  const [sort, setSort] = useState<SortMode>("slowest");
  const [data, setData] = useState<OperationTimings | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (range: number, which: TimingScope) => {
    setLoading(true);
    try {
      setData(await proxyAdminApi.getTimings(range, which));
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || "Lecture des délais impossible" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days, scope);
  }, [days, scope, load]);

  const busiestDay = useMemo(() => {
    if (!data?.daily?.length) return null;
    return data.daily.reduce((best, d) => (d.total_ms > best.total_ms ? d : best), data.daily[0]);
  }, [data]);

  return (
    <div className="flex flex-col gap-4 p-4 w-full overflow-y-auto">
      <div className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">Délais ServiceBox &amp; Alpha</h1>
          <div className="text-h4 text-neutral-400">
            Chaque transfert, création, suppression et envoi d'interventions est chronométré.
          </div>
        </div>
        <Segmented
          value={String(days)}
          options={RANGES.map((r) => ({ value: String(r), label: `${r} jours` }))}
          onChange={(v) => setDays(Number(v))}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Segmented value={scope} options={SCOPES.map((o) => ({ value: o.value, label: o.label }))} onChange={setScope} />
        <span className="text-h5 text-neutral-400">{SCOPES.find((o) => o.value === scope)?.hint}</span>
      </div>

      {loading && !data ? (
        <div className="flex flex-row items-center gap-3 p-4">
          <div className="w-5 h-5 rounded-full border-[3px] border-neutral-200 border-t-neutral-700 animate-spin" />
          <span className="text-h3 text-neutral-500">Lecture des opérations…</span>
        </div>
      ) : !data ? null : (
        <>
          <div className="flex flex-row gap-3 flex-wrap">
            <StatTile
              label="Attente cumulée"
              value={fmtDuration(data.overall.total_ms)}
              hint={`${data.overall.count} opération(s) chronométrée(s)`}
            />
            <StatTile label="Durée moyenne" value={fmtDuration(data.overall.avg_ms)} />
            <StatTile
              label="p95"
              value={fmtDuration(data.overall.p95_ms)}
              hint="1 opération sur 20 est plus lente"
            />
            <StatTile
              label="Pire journée"
              value={busiestDay ? fmtDuration(busiestDay.total_ms) : "—"}
              hint={busiestDay ? fmtDay(busiestDay.day) : undefined}
            />
          </div>

          <DailyWaitChart daily={data.daily} />

          {/* The robots are never hidden, only kept off the chart: they hold the proxy
              lock, so whoever clicks during an autotest queues behind it. */}
          {scope === "human" && data.automatic.count > 0 && (
            <div className="text-h4 text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3">
              En plus :{" "}
              <span className="font-medium text-neutral-700">{data.automatic.count} opération(s) automatique(s)</span>{" "}
              ({fmtDuration(data.automatic.total_ms)} cumulées) — autotests, synchros, tâches planifiées. Personne ne les
              attend, mais elles occupent le proxy, qui ne traite qu'une opération à la fois par compte ServiceBox
              {data.automatic.by_operation[0]
                ? ` — la plus coûteuse : ${opLabel(data.automatic.by_operation[0].operation)} (${fmtDuration(
                    data.automatic.by_operation[0].total_ms,
                  )}).`
                : "."}
            </div>
          )}

          <div className="flex flex-row gap-3 flex-wrap items-start">
            <StatsTable
              title="Par action"
              subtitle="Ce qui coûte le plus de temps au total, pas seulement en moyenne."
              rows={data.by_operation.map((o) => ({ key: o.operation, label: opLabel(o.operation), stats: o }))}
            />
            {scope !== "auto" && (
              <StatsTable
                title="Par utilisateur"
                subtitle="Qui attend, et combien."
                rows={data.by_user.map((u) => ({ key: u.operator_email || "auto", label: userLabel(u.operator_email), stats: u }))}
              />
            )}
          </div>

          <OperationsList slowest={data.slowest} recent={data.recent} sort={sort} onSortChange={setSort} />

          {data.truncated && (
            <div className="text-h4 text-amber-700">
              Trop d'opérations sur la période : les chiffres sont tronqués, réduisez la fenêtre.
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ServiceBoxTimings;
